import { createServerFn } from "@tanstack/react-start";

export type PageMediaSlot = {
  desktopUrl: string | null;
  mobileUrl: string | null;
  alt: string | null;
  credit: string | null;
  overlayOpacity: number;
  glowOpacity: number;
};

export type PublicPageMedia = Record<string, PageMediaSlot>;

async function signPath(path: string | null): Promise<string | null> {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data } = await supabaseAdmin.storage
    .from("page-media")
    .createSignedUrl(path, 60 * 60 * 24 * 7);
  return data?.signedUrl ?? null;
}

export const getPageMedia = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicPageMedia> => {
    const { cached } = await import("@/lib/server-cache.server");
    return cached("page-media", 300_000, async () => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("page_media")
      .select("key, desktop_path, mobile_path, alt, credit, overlay_opacity, glow_opacity");

    const out: PublicPageMedia = {};
    for (const row of data ?? []) {
      const [desktopUrl, mobileUrl] = await Promise.all([
        signPath(row.desktop_path as string | null),
        signPath(row.mobile_path as string | null),
      ]);
      out[row.key as string] = {
        desktopUrl,
        mobileUrl,
        alt: (row.alt as string | null) ?? null,
        credit: ((row as Record<string, unknown>).credit as string | null) ?? null,
        overlayOpacity: Number(row.overlay_opacity ?? 1),
        glowOpacity: Number(row.glow_opacity ?? 1),
      };
    }
    return out;
    });
  },
);
